/**
 * Admin Home Settings Page
 * Allows admin to update home page content including logo, hero section, success stories, and videos
 * Includes home settings editor for platform content
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { 
  Settings, ArrowLeft, Save, Image, Video, Users, FileText,
  Upload, Eye, Camera, Play
} from 'lucide-react';

export default function AdminHomeSettings() {
  const navigate = useNavigate();
  
  // Home page content state
  const [homeContent, setHomeContent] = useState({
    // Top Section / Hero Section
    heroSection: {
      logo: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/adcee6c3-56f6-4254-9122-b7652ab03cf2.jpg",
      centerImage: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/7037d272-5fe9-4c08-a39c-be5798d1462f.jpg",
      title: "Rab Ki Jodi Bina Dowry",
      subtitle: "Relation Forever",
      description: "Find your perfect life partner without the burden of dowry. Join thousands of couples who found true love through our platform."
    },
    
    // Success Stories
    successStories: [
      {
        id: 1,
        coupleNames: "Rahul & Priya",
        location: "Delhi, India",
        story: "We found each other on RJBD and connected instantly. Our families supported our decision to marry without dowry, and we've never been happier!",
        marriedDate: "Married 2023",
        duration: "2 years together",
        image: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/095c21ba-a947-422b-a3a0-2b73ec5da02b.jpg"
      },
      {
        id: 2,
        coupleNames: "Amit & Sneha",
        location: "Mumbai, India",
        story: "RJBD helped us find true love based on compatibility, not financial demands. Our marriage is built on mutual respect and understanding.",
        marriedDate: "Married 2022",
        duration: "3 years together",
        image: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/9afe0198-681b-4874-a5a4-571aaf1ccf84.jpg"
      },
      {
        id: 3,
        coupleNames: "Vikram & Anjali",
        location: "Bangalore, India",
        story: "We're grateful to RJBD for bringing us together. Our love story proves that dowry-free marriages are not just possible, but beautiful!",
        marriedDate: "Married 2024",
        duration: "6 months together",
        image: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/9f00a705-859b-4792-8db9-2965f1c640ea.jpg"
      }
    ],
    
    // Motivational Videos
    motivationalVideos: [
      {
        id: 1,
        title: "Breaking the Chains of Dowry",
        thumbnail: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/9e2f23c5-e1c7-4f11-9c16-d1d0e4077654.jpg",
        views: "15.2K",
        likes: "2.1K",
        duration: "4:30"
      },
      {
        id: 2,
        title: "Love Without Price Tag",
        thumbnail: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/15f90c67-1c4d-4b4c-8c18-244913b0bb95.jpg",
        views: "12.8K",
        likes: "1.8K",
        duration: "3:45"
      },
      {
        id: 3,
        title: "Empowering Women, Ending Dowry",
        thumbnail: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/0249c2a8-6fb8-4d85-881c-fc340927cefa.jpg",
        views: "18.5K",
        likes: "2.7K",
        duration: "5:15"
      },
      {
        id: 4,
        title: "Modern Relationships, Traditional Values",
        thumbnail: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/f36600ca-f86b-4fa2-a806-3601508ec301.jpg",
        views: "9.7K",
        likes: "1.3K",
        duration: "4:10"
      }
    ]
  });

  const [activeTab, setActiveTab] = useState('hero');

  const handleSave = () => {
    // In a real app, this would save to backend/database
    alert('Home page settings saved successfully!');
    // Redirect to home page to see changes
    navigate('/');
  };

  const handlePreview = () => {
    window.open('/', '_blank');
  };

  const updateHeroField = (field: string, value: string) => {
    setHomeContent(prev => ({
      ...prev,
      heroSection: {
        ...prev.heroSection,
        [field]: value
      }
    }));
  };

  const updateSuccessStory = (index: number, field: string, value: string) => {
    setHomeContent(prev => ({
      ...prev,
      successStories: prev.successStories.map((story, i) => 
        i === index ? { ...story, [field]: value } : story
      )
    }));
  };

  const updateVideo = (index: number, field: string, value: string) => {
    setHomeContent(prev => ({
      ...prev,
      motivationalVideos: prev.motivationalVideos.map((video, i) => 
        i === index ? { ...video, [field]: value } : video
      )
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Home Page Settings</h1>
            <p className="text-gray-600">Update and manage your home page content</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button 
              onClick={handlePreview}
              variant="outline" 
              className="bg-transparent border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Home
            </Button>
            <Button 
              onClick={handleSave}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
            <Button 
              onClick={() => navigate('/admin')}
              variant="outline" 
              className="bg-transparent border-gray-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
          </div>
        </div>

        {/* Settings Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 mb-8 bg-slate-100 p-1 rounded-lg">
            <TabsTrigger value="hero" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Image className="w-4 h-4 mr-2" />
              Hero Section
            </TabsTrigger>
            <TabsTrigger value="stories" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Success Stories
            </TabsTrigger>
            <TabsTrigger value="videos" className="data-[state=active]:bg-pink-600 data-[state=active]:text-white">
              <Video className="w-4 h-4 mr-2" />
              Motivational Videos
            </TabsTrigger>
            <TabsTrigger value="preview" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
              <Eye className="w-4 h-4 mr-2" />
              Live Preview
            </TabsTrigger>
          </TabsList>

          {/* Hero Section Tab */}
          <TabsContent value="hero">
            <Card>
              <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Hero Section Settings
                </CardTitle>
                <CardDescription className="text-blue-100">
                  Update the main banner and branding of your home page
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Logo Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Logo & Branding</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-sm font-medium">Logo Image URL</Label>
                      <Input
                        value={homeContent.heroSection.logo}
                        onChange={(e) => updateHeroField('logo', e.target.value)}
                        placeholder="Enter logo image URL"
                        className="mt-1"
                      />
                      <div className="mt-2 p-4 border rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-2">Current Logo:</p>
                        <img 
                          src={homeContent.heroSection.logo} 
                          alt="Logo Preview" 
                          className="h-16 object-contain"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Center Couple Image URL</Label>
                      <Input
                        value={homeContent.heroSection.centerImage}
                        onChange={(e) => updateHeroField('centerImage', e.target.value)}
                        placeholder="Enter center image URL"
                        className="mt-1"
                      />
                      <div className="mt-2 p-4 border rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-2">Current Center Image:</p>
                        <img 
                          src={homeContent.heroSection.centerImage} 
                          alt="Center Image Preview" 
                          className="h-16 object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Text Content */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Text Content</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Main Title</Label>
                      <Input
                        value={homeContent.heroSection.title}
                        onChange={(e) => updateHeroField('title', e.target.value)}
                        placeholder="Enter main title"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Subtitle</Label>
                      <Input
                        value={homeContent.heroSection.subtitle}
                        onChange={(e) => updateHeroField('subtitle', e.target.value)}
                        placeholder="Enter subtitle"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Description</Label>
                      <Textarea
                        value={homeContent.heroSection.description}
                        onChange={(e) => updateHeroField('description', e.target.value)}
                        placeholder="Enter description"
                        className="mt-1 min-h-[100px]"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="p-4 bg-gradient-to-br from-purple-600 via-pink-500 to-rose-500 rounded-lg text-white">
                  <h4 className="font-semibold mb-2">Live Preview:</h4>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{homeContent.heroSection.title}</p>
                    <p className="text-rose-100">{homeContent.heroSection.subtitle}</p>
                    <p className="text-sm mt-2 opacity-90">{homeContent.heroSection.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Success Stories Tab */}
          <TabsContent value="stories">
            <Card>
              <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Success Stories Management
                </CardTitle>
                <CardDescription className="text-purple-100">
                  Update success stories content and images
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {homeContent.successStories.map((story, index) => (
                  <div key={story.id} className="border rounded-lg p-6 bg-white">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Success Story #{index + 1}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column - Image */}
                      <div>
                        <Label className="text-sm font-medium">Story Image URL</Label>
                        <Input
                          value={story.image}
                          onChange={(e) => updateSuccessStory(index, 'image', e.target.value)}
                          placeholder="Enter image URL"
                          className="mt-1"
                        />
                        <div className="mt-2 p-4 border rounded-lg bg-gray-50">
                          <p className="text-sm text-gray-600 mb-2">Current Image:</p>
                          <img 
                            src={story.image} 
                            alt={`Story ${index + 1} Preview`} 
                            className="h-32 w-full object-cover rounded"
                          />
                        </div>
                      </div>

                      {/* Right Column - Text Content */}
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm font-medium">Couple Names</Label>
                          <Input
                            value={story.coupleNames}
                            onChange={(e) => updateSuccessStory(index, 'coupleNames', e.target.value)}
                            placeholder="Enter couple names"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Location</Label>
                          <Input
                            value={story.location}
                            onChange={(e) => updateSuccessStory(index, 'location', e.target.value)}
                            placeholder="Enter location"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Story Content</Label>
                          <Textarea
                            value={story.story}
                            onChange={(e) => updateSuccessStory(index, 'story', e.target.value)}
                            placeholder="Enter success story"
                            className="mt-1 min-h-[100px]"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm font-medium">Married Date</Label>
                            <Input
                              value={story.marriedDate}
                              onChange={(e) => updateSuccessStory(index, 'marriedDate', e.target.value)}
                              placeholder="Enter married date"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-medium">Duration</Label>
                            <Input
                              value={story.duration}
                              onChange={(e) => updateSuccessStory(index, 'duration', e.target.value)}
                              placeholder="Enter duration"
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Motivational Videos Tab */}
          <TabsContent value="videos">
            <Card>
              <CardHeader className="bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Motivational Videos Management
                </CardTitle>
                <CardDescription className="text-pink-100">
                  Update motivational videos content and thumbnails
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {homeContent.motivationalVideos.map((video, index) => (
                    <div key={video.id} className="border rounded-lg p-6 bg-white">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        Video #{index + 1}
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm font-medium">Video Title</Label>
                          <Input
                            value={video.title}
                            onChange={(e) => updateVideo(index, 'title', e.target.value)}
                            placeholder="Enter video title"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Thumbnail Image URL</Label>
                          <Input
                            value={video.thumbnail}
                            onChange={(e) => updateVideo(index, 'thumbnail', e.target.value)}
                            placeholder="Enter thumbnail URL"
                            className="mt-1"
                          />
                          <div className="mt-2 p-4 border rounded-lg bg-gray-50">
                            <p className="text-sm text-gray-600 mb-2">Current Thumbnail:</p>
                            <img 
                              src={video.thumbnail} 
                              alt={`Video ${index + 1} Thumbnail`} 
                              className="h-24 w-full object-cover rounded"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label className="text-sm font-medium">Views</Label>
                            <Input
                              value={video.views}
                              onChange={(e) => updateVideo(index, 'views', e.target.value)}
                              placeholder="Enter views"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-medium">Likes</Label>
                            <Input
                              value={video.likes}
                              onChange={(e) => updateVideo(index, 'likes', e.target.value)}
                              placeholder="Enter likes"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-medium">Duration</Label>
                            <Input
                              value={video.duration}
                              onChange={(e) => updateVideo(index, 'duration', e.target.value)}
                              placeholder="Enter duration"
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Live Preview Tab */}
          <TabsContent value="preview">
            <Card>
              <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Live Preview
                </CardTitle>
                <CardDescription className="text-green-100">
                  Preview your changes before saving
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-center space-y-6">
                  <div className="p-6 bg-gradient-to-br from-purple-600 via-pink-500 to-rose-500 rounded-lg text-white">
                    <h2 className="text-3xl font-bold mb-2">{homeContent.heroSection.title}</h2>
                    <p className="text-xl text-rose-100 mb-4">{homeContent.heroSection.subtitle}</p>
                    <p className="text-lg">{homeContent.heroSection.description}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {homeContent.successStories.map((story) => (
                      <div key={story.id} className="border rounded-lg p-4 bg-white">
                        <img 
                          src={story.image} 
                          alt={story.coupleNames}
                          className="w-full h-48 object-cover rounded mb-4"
                        />
                        <h3 className="font-semibold text-lg">{story.coupleNames}</h3>
                        <p className="text-gray-600 text-sm">{story.location}</p>
                        <p className="text-gray-700 mt-2 text-sm">{story.story}</p>
                        <div className="flex justify-between text-xs text-gray-500 mt-3">
                          <span>{story.marriedDate}</span>
                          <span>{story.duration}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {homeContent.motivationalVideos.map((video) => (
                      <div key={video.id} className="border rounded-lg p-4 bg-white">
                        <img 
                          src={video.thumbnail} 
                          alt={video.title}
                          className="w-full h-32 object-cover rounded mb-3"
                        />
                        <h4 className="font-semibold text-sm mb-2">{video.title}</h4>
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>{video.views} views</span>
                          <span>{video.likes} likes</span>
                          <span>{video.duration}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-4 justify-center">
                    <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                      <Save className="w-4 h-4 mr-2" />
                      Save All Changes
                    </Button>
                    <Button onClick={handlePreview} variant="outline" className="bg-transparent border-blue-300 text-blue-700">
                      <Eye className="w-4 h-4 mr-2" />
                      Open Live Home Page
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Card className="mt-8 border-2 border-gray-200 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">Configure the front page experience</h3>
              <p className="mt-2 text-sm text-gray-600">Use the tabs above to update hero content, success stories, and videos for the home page.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}